import { Link, useParams } from "react-router-dom";

import { ApiError, errorMessage } from "../lib/api";
import { pluralise, relativeDate } from "../lib/format";
import { useCollection, useEntries, useRefreshQuarry } from "../lib/queries";
import { EmptyState } from "../components/EmptyState";
import { EntryCard } from "../components/EntryCard";
import { ErrorState } from "../components/ErrorState";
import { ProgressBand } from "../components/ProgressBand";
import { QuarryList } from "../components/QuarryList";
import { EntryCardSkeleton, LoadingAnnouncement, Skeleton } from "../components/Skeleton";
import { useToasts } from "../components/Toasts";
import { NotFound } from "./NotFound";

/** One volume: the hunt list, and the finds already pressed into it. */
export function CollectionDetail() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const { push } = useToasts();

  const collection = useCollection(collectionId);
  const refresh = useRefreshQuarry(collectionId ?? "");
  const finds = useEntries(collectionId ? { collectionId } : {}, Boolean(collectionId));

  if (collection.error instanceof ApiError && collection.error.isNotFound) {
    return <NotFound title="No such volume" />;
  }

  if (collection.isPending) {
    return (
      <div>
        <LoadingAnnouncement>Fetching the collection…</LoadingAnnouncement>
        <Skeleton className="mb-4 h-4 w-28" />
        <Skeleton className="h-10 w-3/5" />
        <Skeleton className="mt-3 h-3 w-2/5" />
        <Skeleton className="mt-6 h-2.5 w-full" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <EntryCardSkeleton />
          <EntryCardSkeleton />
        </div>
      </div>
    );
  }

  if (collection.isError || !collection.data) {
    return <ErrorState error={collection.error} onRetry={() => void collection.refetch()} />;
  }

  const volume = collection.data;

  const onRefresh = () => {
    refresh.mutate(undefined, {
      onSuccess: () =>
        push({
          title: "Asking Claude for a fresh hunt list.",
          body: "It'll fill in here on its own in a few seconds.",
        }),
      onError: (error) =>
        push({ kind: "problem", title: "Couldn't ask for suggestions.", body: errorMessage(error) }),
    });
  };

  const entries = (finds.data?.pages ?? []).flatMap((page) => page.items);

  return (
    <div>
      <Link
        to="/collections"
        className="touch mb-5 inline-flex items-center gap-2 font-label text-sm text-ink-soft hover:text-ink"
      >
        <span aria-hidden="true">←</span> Collections
      </Link>

      <header className="sheet grain-strong relative overflow-hidden">
        <div className="relative px-4 py-6 sm:px-8 sm:py-8">
          <p className="field-label text-teal">
            {volume.rankLabel} · {volume.geoLabel}
          </p>
          <h1 className="mt-3 display-1 text-[2.5rem] leading-[0.98] text-ink sm:text-[3.5rem]">
            {volume.title}
          </h1>
          {volume.subtitle ? (
            <p className="mt-3 max-w-prose font-prose text-lg leading-relaxed text-ink-soft">
              {volume.subtitle}
            </p>
          ) : null}

          <ProgressBand
            found={volume.foundCount}
            total={volume.quarryCount}
            plural={volume.rankPlural}
            bonusCount={volume.bonusCount}
            className="mt-6 max-w-xl"
          />

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="font-label text-[0.625rem] tracking-[0.2em] text-ink-soft uppercase">
              Opened {relativeDate(volume.createdAt).toLowerCase()}
            </p>
            {volume.quarryState === "ready" ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refresh.isPending}
                className="touch font-label text-xs tracking-wide text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink disabled:opacity-60"
              >
                {refresh.isPending ? "Asking…" : "Suggest different things"}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <section className="mt-10">
        <QuarryList collection={volume} onRefresh={onRefresh} refreshing={refresh.isPending} />
      </section>

      <section className="mt-14">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b-2 border-rule pb-2">
          <h2 className="field-label text-ink">Pressed into this volume</h2>
          {entries.length > 0 ? (
            <p className="font-prose text-sm text-ink-soft">{pluralise(entries.length, "page")}</p>
          ) : null}
        </div>

        {finds.isError ? (
          <ErrorState error={finds.error} onRetry={() => void finds.refetch()} />
        ) : null}

        {finds.isPending ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <EntryCardSkeleton key={index} />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState title="Nothing pressed in here yet" motif="sheet">
            <p>
              This volume opened itself, but none of your finds have landed in it yet. Tick something
              off the list above and it'll appear here.
            </p>
          </EmptyState>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map((entry) => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}

        {finds.hasNextPage ? (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => void finds.fetchNextPage()}
              disabled={finds.isFetchingNextPage}
              className="touch border border-ink bg-card px-5 py-2.5 font-label text-sm tracking-wide text-ink transition-colors hover:bg-paper-deep disabled:opacity-60"
            >
              {finds.isFetchingNextPage ? "Turning the page…" : "Earlier finds"}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
